<script setup>
import { computed, ref, watch } from 'vue';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
import FullCalendar from '@fullcalendar/vue3';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';

const props = defineProps({
  weekStart: { type: String, required: true }, // ISO string
  schedules: { type: Array, default: () => [] },
  exceptions: { type: Array, default: () => [] },
  timezone: { type: String, default: 'UTC' },
  allowCreate: { type: Boolean, default: true },
  agentColors: {
    type: Object,
    default: () => ({}),
  },
  highlightToday: { type: Boolean, default: true },
});

const emit = defineEmits([
  'create:schedule',
  'edit:schedule',
  'edit:exception',
]);
const { t, locale } = useI18n();

const calendarRef = ref(null);
const weekStartDate = computed(() => dayjs(props.weekStart).startOf('day'));

const parsedLocale = computed(() => {
  const value = String(locale.value || 'en').replace('_', '-');
  return value || 'en';
});

const parseTimeToMinutes = value => {
  if (!value) return 0;
  const [hoursPart, minutesPart] = String(value).split(':');
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart || 0);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
  return hours * 60 + minutes;
};

const startEndFromDate = (
  dateValue,
  startTime,
  endTime,
  allDayFallback = false
) => {
  const date = dayjs(dateValue).startOf('day');
  if (!date.isValid()) return null;

  if (allDayFallback && !startTime && !endTime) {
    return {
      start: date,
      end: date.add(1, 'day'),
    };
  }

  const startMinutes = parseTimeToMinutes(startTime || '00:00');
  const endMinutes = parseTimeToMinutes(endTime || '24:00');
  const start = date.add(startMinutes, 'minute');
  let end = date.add(endMinutes, 'minute');
  if (!end.isAfter(start)) {
    end = end.add(1, 'day');
  }

  return { start, end };
};

const colorForItem = item => {
  const option = item.color || props.agentColors?.[Number(item.user_id)];
  return {
    backgroundColor: option?.bgHex || '#111827',
    borderColor: option?.borderHex || option?.bgHex || '#111827',
    textColor: option?.textHex || '#ffffff',
  };
};

const scheduleEvents = computed(() => {
  return (props.schedules || [])
    .filter(
      item => item.weekday_index !== null && item.weekday_index !== undefined
    )
    .map(item => {
      const weekday = Number(item.weekday_index);
      const day = weekStartDate.value.add(weekday, 'day');
      const range = startEndFromDate(day, item.start_time, item.end_time);
      if (!range) return null;

      const color = colorForItem(item);
      return {
        id: `schedule-${item.id}`,
        title: item.agent_name || item.agent || `Agent ${item.user_id}`,
        start: range.start.toISOString(),
        end: range.end.toISOString(),
        backgroundColor: color.backgroundColor,
        borderColor: color.borderColor,
        textColor: color.textColor,
        classNames: ['workforce-fc-event', 'workforce-fc-event-schedule'],
        extendedProps: {
          recordType: 'schedule',
          raw: item,
        },
      };
    })
    .filter(Boolean);
});

const exceptionStatusLabel = status => {
  if (!status) return '';
  const key = String(status).toUpperCase();
  const i18nKey = `WORKFORCE.EXCEPTIONS.STATUS.${key}`;
  const translated = t(i18nKey);
  return translated === i18nKey ? status : translated;
};

const exceptionEvents = computed(() => {
  const weekStart = weekStartDate.value;
  const weekEnd = weekStart.add(7, 'day');

  return (props.exceptions || [])
    .map(item => {
      let eventDate = null;
      if (item.date) {
        eventDate = dayjs(item.date).startOf('day');
      } else if (
        item.weekday_index !== null &&
        item.weekday_index !== undefined
      ) {
        eventDate = weekStart.add(Number(item.weekday_index), 'day');
      }

      if (!eventDate || !eventDate.isValid()) return null;
      if (eventDate.isBefore(weekStart) || !eventDate.isBefore(weekEnd)) {
        return null;
      }

      const range = startEndFromDate(
        eventDate,
        item.start_time,
        item.end_time,
        true
      );
      if (!range) return null;

      const color = colorForItem(item);
      const agentName =
        item.agent_name || item.user?.name || `Agent ${item.user_id}`;
      return {
        id: `exception-${item.id}`,
        title: `${agentName} · ${exceptionStatusLabel(item.status)}`,
        start: range.start.toISOString(),
        end: range.end.toISOString(),
        backgroundColor: color.backgroundColor,
        borderColor: color.borderColor,
        textColor: color.textColor,
        classNames: ['workforce-fc-event', 'workforce-fc-event-exception'],
        extendedProps: {
          recordType: 'exception',
          raw: item,
        },
      };
    })
    .filter(Boolean);
});

const calendarEvents = computed(() => [
  ...scheduleEvents.value,
  ...exceptionEvents.value,
]);

const onDateClick = info => {
  if (!props.allowCreate) return;
  const clickedAt = dayjs(info.date);
  emit('create:schedule', {
    weekday: clickedAt.day(),
    startMinutes: clickedAt.hour() * 60 + clickedAt.minute(),
  });
};

const onSelect = info => {
  if (!props.allowCreate) return;
  const selectedAt = dayjs(info.start);
  emit('create:schedule', {
    weekday: selectedAt.day(),
    startMinutes: selectedAt.hour() * 60 + selectedAt.minute(),
  });
  info.view.calendar.unselect();
};

const onEventClick = info => {
  const { recordType, raw } = info.event.extendedProps || {};
  if (!raw) return;
  if (recordType === 'exception') {
    emit('edit:exception', raw);
    return;
  }
  emit('edit:schedule', raw);
};

const calendarOptions = computed(() => ({
  plugins: [timeGridPlugin, interactionPlugin],
  initialView: 'timeGridWeek',
  initialDate: weekStartDate.value.toDate(),
  timeZone: props.timezone || 'UTC',
  locale: parsedLocale.value,
  firstDay: 0,
  allDaySlot: false,
  slotDuration: '00:30:00',
  snapDuration: '00:30:00',
  slotMinTime: '00:00:00',
  slotMaxTime: '24:00:00',
  expandRows: true,
  nowIndicator: true,
  selectable: props.allowCreate,
  selectMirror: false,
  editable: false,
  headerToolbar: false,
  dayHeaderFormat: { weekday: 'short', month: 'short', day: 'numeric' },
  slotLabelFormat: {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  },
  eventTimeFormat: {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  },
  events: calendarEvents.value,
  dateClick: onDateClick,
  select: onSelect,
  eventClick: onEventClick,
  dayCellClassNames: args => {
    if (!props.highlightToday) return [];
    return dayjs(args.date).isSame(dayjs(), 'day')
      ? ['workforce-fc-today']
      : [];
  },
  height: 'auto',
}));

const syncCalendarWeek = () => {
  const api = calendarRef.value?.getApi();
  if (!api) return;
  api.gotoDate(weekStartDate.value.toDate());
};

watch(
  () => props.weekStart,
  () => {
    syncCalendarWeek();
  }
);
</script>

<template>
  <div class="workforce-calendar-wrapper">
    <FullCalendar ref="calendarRef" :options="calendarOptions" />
  </div>
</template>

<style scoped>
.workforce-calendar-wrapper {
  border: 1px solid rgba(71, 85, 105, 0.45);
  border-radius: 0.75rem;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.18);
}

:deep(.fc) {
  --fc-border-color: rgba(100, 116, 139, 0.38);
  --fc-now-indicator-color: #f43f5e;
  --fc-page-bg-color: transparent;
  --fc-neutral-bg-color: rgba(15, 23, 42, 0.3);
  font-size: 12px;
}

:deep(.fc .fc-scrollgrid) {
  border: 0;
}

:deep(.fc .fc-col-header-cell) {
  background: rgba(30, 41, 59, 0.4);
}

:deep(.fc .fc-col-header-cell-cushion) {
  padding: 10px 8px;
  font-weight: 600;
  color: rgb(226, 232, 240);
}

:deep(.fc .fc-timegrid-axis-cushion) {
  color: rgb(148, 163, 184);
  font-size: 11px;
}

:deep(.fc .fc-timegrid-slot-label-cushion) {
  color: rgb(148, 163, 184);
}

:deep(.fc .fc-timegrid-slot) {
  height: 2.9rem;
}

:deep(.fc .fc-timegrid-col-frame) {
  min-height: 68rem;
}

:deep(.fc .fc-timegrid-col) {
  background: rgba(15, 23, 42, 0.2);
}

:deep(.fc .fc-timegrid-col.workforce-fc-today) {
  background: rgba(51, 65, 85, 0.42);
}

:deep(.workforce-fc-event) {
  border-radius: 8px;
  border-width: 1px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.32);
  padding: 2px 4px;
}

:deep(.workforce-fc-event .fc-event-main) {
  padding: 1px 2px;
}

:deep(.workforce-fc-event .fc-event-title) {
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
}

:deep(.workforce-fc-event-exception) {
  border-style: dashed;
}

:deep(.fc .fc-timegrid-now-indicator-line) {
  border-width: 1px;
}

:deep(.fc .fc-timegrid-now-indicator-arrow) {
  border-top-color: #f43f5e;
  border-bottom-color: #f43f5e;
}
</style>
